import {
  CELL_LABEL,
  DEFAULT_OPS_FILTERS,
  ETAT_HEALTH,
  ETAT_LABEL,
  OPS_CELLS,
  OPS_PRM,
  cellRows,
  cellStats,
  compareMonths,
  dateLong,
  entityBySync,
  entityTable,
  eurK,
  eurKSigned,
  filterOps,
  firstCell,
  headline,
  opsPeople,
  parseWho,
  prevMonthEnd,
  pcm,
  rateOf,
  ptxt,
  summarize,
  type OpsCell,
  type OpsChantier,
  type OpsHealth,
  type OpsPrev,
} from '@cockpit/shared';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Spinner,
  Table,
  Td,
  Th,
  cn,
  type Tone,
} from '@cockpit/ui';
import { ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Agenda } from '../components/ops/Agenda.js';
import { Billing } from '../components/ops/Billing.js';
import { FicheDrawer } from '../components/ops/FicheDrawer.js';
import { PageHeader } from '../components/Layout.js';
import { dateTime } from '../lib/format.js';
import { useOps } from '../lib/queries.js';

const HEALTH_TONE: Record<OpsHealth, Tone> = { crit: 'danger', surv: 'warn', bon: 'success' };
const HEALTH_BAR: Record<OpsHealth, string> = {
  crit: 'bg-danger-600',
  surv: 'bg-warn-600',
  bon: 'bg-success-600',
};
const CELL_TONE = {
  r: 'border-danger-100 bg-danger-50',
  a: 'border-warn-100 bg-warn-50',
  g: 'border-success-100 bg-success-50',
} as const;
const CELL_NUM = { r: 'text-danger-700', a: 'text-warn-700', g: 'text-success-700' } as const;

const entLabel = (code: string) => entityBySync(code)?.label ?? code;

const RULES: [string, string][] = [
  [
    'En dérive',
    `marge à terminaison à plus de ${OPS_PRM.der} points sous la marge prévue, ou chantier à perte (marge à terminaison négative).`,
  ],
  [
    'À surveiller',
    `pas en dérive, mais au moins un point à régler : facturation en retard (avancement en avance d’au moins ${OPS_PRM.bill} points sur le facturé, et au moins 1 k€ facturable), fin prévue dépassée avec au moins 500 € à facturer ou à dépenser, démarrage sous 30 jours sans conducteur, données à vérifier, ou marge en léger recul (${OPS_PRM.rec} à ${OPS_PRM.der} points).`,
  ],
  ['Bons', 'aucune de ces situations.'],
  [
    'Pilotables',
    `facturés à moins de ${OPS_PRM.pil} % : la marge peut encore bouger. En fin de chantier : facturés à ${OPS_PRM.pil} % et plus, on suit l’atterrissage.`,
  ],
  [
    'Données à vérifier',
    'écart entre le bilan Flex et la comptabilité (facturé, achats ou sous-traitance), budget révisé absent ou incohérent, budget non révisé depuis plus de 120 jours, ou facturé au-delà du CA revu.',
  ],
  [
    'État de l’entreprise',
    `critique si la marge globale recule d’au moins ${OPS_PRM.der} points ou si 20 % des chantiers sont en dérive ; à surveiller dès qu’un chantier dérive ou que la marge recule d’au moins ${OPS_PRM.rec} point. Même règle pour chaque entité.`,
  ],
];

export function RevueOperationnellePage() {
  const q = useOps();
  const [params, setParams] = useSearchParams();
  const [rules, setRules] = useState(false);
  const dataset = q.data?.dataset;

  const filters = {
    ...DEFAULT_OPS_FILTERS,
    ent: params.get('ent') ?? 'Tous',
    who: params.get('who') ?? '',
    q: params.get('q') ?? '',
  };
  const set = (patch: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };

  const inEnt = useMemo(
    () => (dataset ? filterOps(dataset.rows, { ...filters, who: '', q: '' }) : []),
    [dataset, filters.ent], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const rows = useMemo(
    () => (dataset ? filterOps(dataset.rows, filters) : []),
    [dataset, filters.ent, filters.who, filters.q], // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorState>{(q.error as Error).message}</ErrorState>;
  if (!dataset) return null;
  if (!dataset.rows.length)
    return (
      <EmptyState title="Aucun chantier en cours">
        La synchronisation avec Flex n’a remonté aucun chantier pour votre périmètre.
      </EmptyState>
    );

  const entities = ['Tous', ...Object.keys(dataset.ents).sort()];
  const people = opsPeople(inEnt);
  const who = parseWho(filters.who);
  const s = summarize(rows);
  const stats = cellStats(rows);

  const asked = params.get('cell') as OpsCell | null;
  const cell: OpsCell = asked && OPS_CELLS.includes(asked) ? asked : firstCell(rows);
  const list = cellRows(rows, cell);
  const selected = list.find((r) => r.code === params.get('ch')) ?? list[0] ?? null;
  const fiche = params.get('fiche')
    ? (dataset.rows.find((r) => r.code === params.get('fiche')) ?? null)
    : null;

  const subject = filters.q
    ? 'La sélection'
    : who
      ? `Le portefeuille de ${who.n}`
      : filters.ent === 'Tous'
        ? 'L’entreprise'
        : entLabel(filters.ent);
  const showEntities = filters.ent === 'Tous' && !who && !filters.q;
  const entRows = showEntities ? entityTable(rows, dataset.asOf) : [];

  return (
    <div className="flex flex-col gap-8">
      <div>
        <PageHeader
          eyebrow="Revue opérationnelle"
          title="Chantiers en cours"
          sub={`Situation au ${dateLong(dataset.asOf)} · marge à terminaison, facturation et planning, lus dans Flex`}
          aside={
            <span className="text-[12.5px] text-ink-3" title="Dernière synchronisation avec Flex">
              Synchronisé le {dateTime(q.data!.syncedAt)}
            </span>
          }
        />
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-line-soft bg-surface px-4 py-3 shadow-od-1">
          <label className="flex items-center gap-2 text-[13px] text-ink-2">
            Entité
            <Select
              value={filters.ent}
              onChange={(e) =>
                set({ ent: e.target.value === 'Tous' ? null : e.target.value, who: null, ch: null })
              }
              className="h-9 w-auto min-w-[10rem]"
            >
              {entities.map((e) => (
                <option key={e} value={e}>
                  {e === 'Tous' ? 'Toutes les entités' : entLabel(e)}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex items-center gap-2 text-[13px] text-ink-2">
            Responsable
            <Select
              value={filters.who}
              onChange={(e) => set({ who: e.target.value || null, ch: null })}
              className="h-9 w-auto min-w-[12rem]"
            >
              <option value="">Tous</option>
              {people.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label} · {p.n}
                </option>
              ))}
            </Select>
          </label>
          <div className="relative min-w-[14rem] flex-1">
            <Search
              size={16}
              strokeWidth={1.75}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-3"
              aria-hidden
            />
            <Input
              type="search"
              value={filters.q}
              onChange={(e) => set({ q: e.target.value || null, ch: null })}
              placeholder="Chantier, client ou code affaire"
              aria-label="Rechercher un chantier"
              className="h-9 w-full pl-9"
            />
          </div>
        </div>
      </div>

      <Verdict subject={subject} s={s} />
      <Moves rows={rows} prev={q.data!.prev} asOf={dataset.asOf} onOpen={(code) => set({ fiche: code })} />

      <section aria-labelledby="matrice" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="matrice" className="font-display text-[19px] font-bold text-ink">
            Où agir
          </h2>
          <button
            type="button"
            onClick={() => setRules((v) => !v)}
            aria-expanded={rules}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-azur-700 hover:underline"
          >
            Règles de classement
            <ChevronDown
              size={14}
              strokeWidth={2}
              className={cn('transition-transform', rules && 'rotate-180')}
            />
          </button>
        </div>
        {rules && (
          <dl className="grid gap-2 rounded-md bg-surface-2 px-4 py-3 text-[13.5px] text-ink-2">
            {RULES.map(([t, d]) => (
              <div key={t}>
                <dt className="inline font-semibold text-ink">{t} : </dt>
                <dd className="inline">{d}</dd>
              </div>
            ))}
          </dl>
        )}
        <Matrix stats={stats} cell={cell} onPick={(c) => set({ cell: c, ch: null })} />
      </section>

      <section
        aria-label="Chantiers de la case"
        className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]"
      >
        <div className="flex flex-col gap-2">
          <h3 className="font-display text-[16px] font-bold text-ink">
            {ETAT_LABEL[cell[0] as 'r' | 'a' | 'g']} · {CELL_LABEL[cell]}
            <span className="ml-2 text-[13px] font-normal text-ink-3">
              {list.length} chantier{list.length > 1 ? 's' : ''}
            </span>
          </h3>
          {list.length ? (
            <ul className="divide-y divide-line-soft overflow-hidden rounded-md border border-line-soft bg-surface">
              {list.slice(0, 60).map((r) => (
                <li key={r.code}>
                  <button
                    type="button"
                    aria-pressed={selected?.code === r.code}
                    onClick={() => set({ ch: r.code })}
                    className={cn(
                      'flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-surface-2',
                      selected?.code === r.code && 'bg-surface-2 shadow-[inset_3px_0_0_rgb(var(--c-action))]',
                    )}
                  >
                    <span className="flex items-baseline justify-between gap-3">
                      <b className="min-w-0 truncate text-[14.5px] text-ink">{r.nom || r.cli || r.code}</b>
                      <span className="tnum shrink-0 text-[13px] text-ink-2">{eurK(r.CAr)}</span>
                    </span>
                    <span className="flex items-baseline justify-between gap-3 text-[12.5px] text-ink-3">
                      <span className="min-w-0 truncate">
                        {entLabel(r.ent)} · {r.c.reason}
                      </span>
                      <span className="tnum shrink-0">
                        {pcm(rateOf(r.Pv))} → {pcm(rateOf(r.Tm))}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
              {list.length > 60 && (
                <li className="px-4 py-2.5 text-[12.5px] text-ink-3">
                  {list.length - 60} autres chantiers : affinez avec l’entité, le responsable ou la recherche.
                </li>
              )}
            </ul>
          ) : (
            <p className="rounded-md border border-dashed border-line px-4 py-6 text-center text-[13.5px] text-ink-3">
              Aucun chantier dans cette case.
            </p>
          )}
        </div>
        <Detail r={selected} onOpen={(code) => set({ fiche: code })} />
      </section>

      <Agenda rows={rows} asOf={dataset.asOf} onOpen={(code) => set({ fiche: code })} />

      <Billing
        rows={rows}
        n1={(dataset.n1 ?? []).filter(
          (x) =>
            (filters.ent === 'Tous' || x.ent === filters.ent) &&
            !who &&
            (!filters.q || `${x.code} ${x.cli} ${x.ds}`.toLowerCase().includes(filters.q.toLowerCase())),
        )}
        asOf={dataset.asOf}
        onOpen={(code) => set({ fiche: code })}
      />

      {entRows.length > 0 && (
        <section aria-labelledby="entites" className="flex flex-col gap-3">
          <h2 id="entites" className="font-display text-[19px] font-bold text-ink">
            Les entités
          </h2>
          <Card padded={false}>
            <Table minWidth={760}>
              <thead>
                <tr>
                  <Th>Entité</Th>
                  <Th>Santé des chantiers</Th>
                  <Th align="right">Chantiers</Th>
                  <Th align="right">En dérive</Th>
                  <Th align="right">Marge prévue → à terminaison</Th>
                  <Th align="right">Reste à facturer</Th>
                  <Th align="right">Démarrages {OPS_PRM.hz} j</Th>
                </tr>
              </thead>
              <tbody>
                {entRows.map((g) => (
                  <tr
                    key={g.ent}
                    tabIndex={0}
                    onClick={() => set({ ent: g.ent, cell: null, ch: null })}
                    onKeyDown={(e) => e.key === 'Enter' && set({ ent: g.ent, cell: null, ch: null })}
                    className="cursor-pointer hover:bg-surface-2 focus-visible:bg-surface-2"
                    aria-label={`Ouvrir ${entLabel(g.ent)}`}
                  >
                    <Td>
                      <span className="flex items-center gap-2 font-display font-bold">
                        <span aria-hidden className={cn('h-2.5 w-2.5 rounded-pill', HEALTH_BAR[g.etat])} />
                        {entLabel(g.ent)}
                      </span>
                    </Td>
                    <Td>
                      <span
                        className="flex h-2.5 w-40 max-w-full gap-0.5 overflow-hidden rounded-pill"
                        aria-hidden
                      >
                        {g.r > 0 && <i className="bg-danger-600" style={{ flex: `${g.r} 1 0` }} />}
                        {g.a > 0 && <i className="bg-warn-600" style={{ flex: `${g.a} 1 0` }} />}
                        {g.g > 0 && <i className="bg-success-600" style={{ flex: `${g.g} 1 0` }} />}
                      </span>
                      <small className="tnum text-[11.5px] text-ink-3">
                        {g.r} · {g.a} · {g.g}
                      </small>
                    </Td>
                    <Td align="right">{g.n}</Td>
                    <Td align="right" className={g.r ? 'text-danger-700' : undefined}>
                      {g.r || '—'}
                    </Td>
                    <Td align="right">
                      {pcm(g.tauxP)} → <b>{pcm(g.tauxT)}</b>
                    </Td>
                    <Td align="right">{eurK(g.raf)}</Td>
                    <Td align="right">{g.starts || '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </section>
      )}

      {dataset.internes.length > 0 && (
        <p className="text-[12.5px] text-ink-3">
          {dataset.internes.length} affaires internes ou intra-groupe (stock, outillage, commandes
          inter-agences) sont exclues de cette revue.
        </p>
      )}

      <FicheDrawer r={fiche} asOf={dataset.asOf} onClose={() => set({ fiche: null })} />
    </div>
  );
}

function Verdict({ subject, s }: { subject: string; s: ReturnType<typeof summarize> }) {
  const tone = HEALTH_TONE[s.etat];
  const label = { crit: 'Critique', surv: 'À surveiller', bon: 'Bon' }[s.etat];
  return (
    <Card
      className={cn(
        'relative overflow-hidden border-l-4',
        { crit: 'border-l-danger-600', surv: 'border-l-warn-600', bon: 'border-l-success-600' }[s.etat],
      )}
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:items-center">
        <div className="flex flex-col gap-2">
          <Badge tone={tone} dot className="self-start">
            {label}
          </Badge>
          <p className="font-display text-[24px] font-bold leading-tight text-ink sm:text-[28px]">
            {headline(s, subject)}
          </p>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4 lg:grid-cols-2">
          <Kpi label="Chantiers" value={String(s.n)} sub={`${s.nP} pilotables`} />
          <Kpi label="En dérive" value={String(s.nR)} sub={`dont ${s.nRp} pilotables`} bad={s.nR > 0} />
          <Kpi
            label="Marge prévue → actuelle"
            value={`${pcm(s.tauxP)} → ${pcm(s.tauxT)}`}
            sub={`${ptxt(s.dG)} · ${eurKSigned(s.margeT - s.margeP)}`}
            bad={s.dG <= -OPS_PRM.rec}
          />
          <Kpi label="Reste à facturer" value={eurK(s.raf)} sub={`sur ${eurK(s.ca)} de CA revu`} />
        </dl>
      </div>
    </Card>
  );
}

/** Depuis le mois dernier : chantiers passés en dérive, revenus au vert, passés en fin de chantier. */
function Moves({
  rows,
  prev,
  asOf,
  onOpen,
}: {
  rows: OpsChantier[];
  prev: OpsPrev | null;
  asOf: string;
  onOpen: (code: string) => void;
}) {
  if (!prev)
    return (
      <p className="text-[13px] text-ink-3">
        Comparaison avec le {dateLong(prevMonthEnd(asOf))} indisponible : la situation du mois précédent n’a
        pas encore été synchronisée.
      </p>
    );
  const m = compareMonths(rows, prev);
  const groups = [
    { list: m.toRed, tone: 'bg-danger-600', label: (n: number) => `${n} passé${n > 1 ? 's' : ''} en dérive` },
    {
      list: m.toGreen,
      tone: 'bg-success-600',
      label: (n: number) => `${n} revenu${n > 1 ? 's' : ''} au vert`,
    },
    {
      list: m.toFinished,
      tone: 'bg-ink-3',
      label: (n: number) => `${n} passé${n > 1 ? 's' : ''} en fin de chantier`,
    },
  ].filter((g) => g.list.length);
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md border border-line-soft bg-surface px-4 py-3 text-[13.5px]">
      <b className="font-display text-ink">Depuis le {dateLong(prev.asOf)}</b>
      {groups.length ? (
        groups.map((g) => (
          <span key={g.tone} className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <i aria-hidden className={cn('h-2.5 w-2.5 rounded-pill', g.tone)} />
            <b>{g.label(g.list.length)} :</b>
            {g.list.slice(0, 6).map((r) => (
              <button
                key={r.code}
                type="button"
                onClick={() => onOpen(r.code)}
                className="font-semibold text-azur-700 underline underline-offset-4"
              >
                {r.nom || r.cli || r.code}
              </button>
            ))}
            {g.list.length > 6 && <span className="text-ink-3">+{g.list.length - 6}</span>}
          </span>
        ))
      ) : (
        <span className="text-ink-3">Aucun chantier n’a changé de case.</span>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, bad }: { label: string; value: string; sub: string; bad?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</dt>
      <dd
        className={cn(
          'tnum font-display text-[22px] font-bold leading-tight',
          bad ? 'text-danger-700' : 'text-ink',
        )}
      >
        {value}
      </dd>
      <dd className="text-[12.5px] text-ink-3">{sub}</dd>
    </div>
  );
}

function Matrix({
  stats,
  cell,
  onPick,
}: {
  stats: ReturnType<typeof cellStats>;
  cell: OpsCell;
  onPick: (c: OpsCell) => void;
}) {
  const by = Object.fromEntries(stats.map((c) => [c.cell, c])) as Record<OpsCell, (typeof stats)[number]>;
  const rowsDef: { e: 'r' | 'a' | 'g'; title: string; sub: string }[] = [
    { e: 'r', title: 'En dérive', sub: 'marge au-dessus de 3 pts sous le prévu, ou à perte' },
    { e: 'a', title: 'À surveiller', sub: 'un point à régler' },
    { e: 'g', title: 'Bons', sub: 'marge et planning tenus' },
  ];
  return (
    <div className="grid grid-cols-[minmax(0,7.5rem)_repeat(2,minmax(0,1fr))] gap-2.5 sm:grid-cols-[11rem_repeat(2,minmax(0,1fr))]">
      <span />
      <div className="px-1 text-[12.5px] font-semibold text-ink-2">
        Pilotables <span className="font-normal text-ink-3">facturés à moins de {OPS_PRM.pil} %</span>
      </div>
      <div className="px-1 text-[12.5px] font-semibold text-ink-2">
        En fin de chantier <span className="font-normal text-ink-3">{OPS_PRM.pil} % et plus</span>
      </div>
      {rowsDef.map((row) => (
        <MatrixRow key={row.e} row={row} by={by} cell={cell} onPick={onPick} />
      ))}
    </div>
  );
}

function MatrixRow({
  row,
  by,
  cell,
  onPick,
}: {
  row: { e: 'r' | 'a' | 'g'; title: string; sub: string };
  by: Record<OpsCell, { n: number; ca: number; dm: number }>;
  cell: OpsCell;
  onPick: (c: OpsCell) => void;
}) {
  return (
    <>
      <div className="flex flex-col justify-center">
        <b className="font-display text-[15px] text-ink">{row.title}</b>
        <small className="hidden text-[12px] text-ink-3 sm:block">{row.sub}</small>
      </div>
      {(['p', 'f'] as const).map((p) => {
        const k = `${row.e}-${p}` as OpsCell;
        const c = by[k];
        const on = cell === k;
        const empty = c.n === 0;
        return (
          <button
            key={k}
            type="button"
            aria-pressed={on}
            onClick={() => onPick(k)}
            className={cn(
              'relative flex min-h-[7rem] flex-col gap-1 rounded-lg border-2 p-3.5 text-left transition-shadow hover:shadow-od-2',
              p === 'p' ? CELL_TONE[row.e] : 'border-line-soft bg-surface-2',
              on && '!border-ink shadow-od-2',
              empty && 'opacity-60',
            )}
          >
            <span
              className={cn(
                'tnum font-display text-[32px] font-bold leading-none',
                p === 'p' ? CELL_NUM[row.e] : 'text-ink-3',
              )}
            >
              {c.n}
            </span>
            {k === 'r-p' && c.n > 0 && (
              <span className="absolute right-3 top-3 text-[11.5px] font-bold uppercase tracking-[0.06em] text-danger-700">
                Priorité
              </span>
            )}
            <span className="text-[13.5px] text-ink">{empty ? 'aucun chantier' : CELL_LABEL[k]}</span>
            {!empty && (
              <span className="tnum text-[12px] text-ink-3">
                {eurK(c.ca)} de CA · {eurKSigned(c.dm)} d’écart de marge
              </span>
            )}
          </button>
        );
      })}
    </>
  );
}

function Detail({ r, onOpen }: { r: OpsChantier | null; onOpen: (code: string) => void }) {
  if (!r)
    return (
      <Card>
        <p className="text-[13.5px] text-ink-3">Aucun chantier dans cette case.</p>
      </Card>
    );
  const c = r.c;
  const tone = HEALTH_TONE[ETAT_HEALTH[c.e]];
  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={tone} dot>
          {ETAT_LABEL[c.e].replace(/s$/, '')}
        </Badge>
        <Badge tone={c.p ? 'navy' : 'neutral'}>{c.p ? 'Pilotable' : 'En fin de chantier'}</Badge>
        <span className="font-mono text-[12px] text-ink-3">
          {r.code} · {entLabel(r.ent)}
          {r.type ? ` · ${r.type}` : ''}
        </span>
      </div>
      <div>
        <h3 className="font-display text-[22px] font-bold leading-tight text-ink">
          {r.nom || r.cli || r.code}
        </h3>
        <p className="mt-1 text-[13.5px] text-ink-2">
          {r.cli}
          {r.cli ? ' · ' : ''}
          {r.cond ? `conducteur ${r.cond}` : <span className="text-danger-700">aucun conducteur</span>}
          {r.com ? ` · commercial ${r.com}` : ''} · fin prévue {r.fin ? dateLong(r.fin) : 'non renseignée'}
        </p>
      </div>
      <dl className="grid grid-cols-3 divide-x divide-line-soft border-y border-line-soft">
        <Mini
          label="Marge prévue → actuelle"
          value={`${pcm(rateOf(r.Pv))} → ${pcm(rateOf(r.Tm))}`}
          sub={Math.abs(c.d) < 0.05 ? '= prévu' : ptxt(c.d)}
          tone={c.d < 0 ? 'bad' : c.d > 0 ? 'good' : undefined}
        />
        <Mini label="Travaux réalisés" value={`${r.cr} %`} sub="du chantier" />
        <Mini
          label="Facturé"
          value={`${r.fa ?? '—'} %`}
          sub={
            c.p
              ? `encore ${OPS_PRM.pil - (r.fa ?? 0)} pts avant le seuil`
              : `seuil de ${OPS_PRM.pil} % dépassé`
          }
        />
      </dl>
      <div>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-ink-3">Pourquoi</p>
        <ul className="mt-1 list-disc pl-5 text-[14px] text-ink">
          {c.why.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      </div>
      {c.fi && <p className="text-[13px] font-semibold text-warn-700">Données à vérifier : {c.fi}</p>}
      <div className={cn('rounded-md px-4 py-3', c.p ? CELL_TONE[c.e] : 'bg-surface-2')}>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-ink-3">
          {c.p ? 'Ce qu’on peut encore faire' : 'Atterrissage'}
        </p>
        {c.todo.map(([t, v]) => (
          <div key={t} className="flex justify-between gap-3 py-0.5 text-[14px]">
            <span>{t}</span>
            <b className="tnum">{v == null ? '' : typeof v === 'string' ? v : eurK(v)}</b>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onOpen(r.code)}
        className="self-start text-[14px] font-semibold text-azur-700 hover:underline"
      >
        Ouvrir la fiche complète du chantier →
      </button>
    </Card>
  );
}

function Mini({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'bad' | 'good';
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 px-3 py-3 first:pl-0">
      <dt className="text-[11.5px] text-ink-3">{label}</dt>
      <dd className="tnum font-display text-[19px] font-bold leading-tight text-ink">{value}</dd>
      <dd
        className={cn(
          'text-[12.5px] font-semibold',
          tone === 'bad' ? 'text-danger-700' : tone === 'good' ? 'text-success-700' : 'text-ink-3',
        )}
      >
        {sub}
      </dd>
    </div>
  );
}
